import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { botService, giftScreenshotService, bilibiliBindingService } from '../services';
import BackButton from '../components/BackButton';
import './GiftScreenshot.css'; // 复用截图页面样式

// API 基础 URL
const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:5000/api' : '/api');

// 大航海头像框映射 (guard_level: 1=总督, 2=提督, 3=舰长)
const guardFrameMap = {
  1: 'https://i0.hdslb.com/bfs/live/a454275dea465ac15a03f121f0d7edaf96e30bcf.png', // 总督
  2: 'https://i0.hdslb.com/bfs/live/09937c3beb0608e267a50ac3c7125c3f2d709098.png', // 提督
  3: 'https://i0.hdslb.com/bfs/live/80f732943cc3367029df65e267960d56736a82ee.png'  // 舰长
};

// 分段时间输入组件（复用自 GiftScreenshot）
const SegmentedTimeInput = ({ label, value, onChange }) => {
  const yearRef = useRef(null);
  const monthRef = useRef(null);
  const dayRef = useRef(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);

  const parseValue = (val) => {
    if (!val) return { year: '', month: '', day: '', hour: '', minute: '' };
    const match = val.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (match) {
      return { year: match[1], month: match[2], day: match[3], hour: match[4], minute: match[5] };
    }
    return { year: '', month: '', day: '', hour: '', minute: '' };
  };

  const parsed = parseValue(value);
  const [year, setYear] = useState(parsed.year);
  const [month, setMonth] = useState(parsed.month);
  const [day, setDay] = useState(parsed.day);
  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);

  useEffect(() => {
    const p = parseValue(value);
    setYear(p.year);
    setMonth(p.month);
    setDay(p.day);
    setHour(p.hour);
    setMinute(p.minute);
  }, [value]);

  const buildValue = (y, m, d, h, min) => {
    if (y && m && d && h !== '' && min !== '') {
      const padMonth = m.padStart(2, '0');
      const padDay = d.padStart(2, '0');
      const padHour = h.padStart(2, '0');
      const padMin = min.padStart(2, '0');
      return `${y}-${padMonth}-${padDay}T${padHour}:${padMin}`;
    }
    return '';
  };

  const handleChange = (field, val, maxLen, nextRef) => {
    const numVal = val.replace(/\D/g, '');
    let newYear = year, newMonth = month, newDay = day, newHour = hour, newMinute = minute;

    switch(field) {
      case 'year': newYear = numVal.slice(0, 4); setYear(newYear); break;
      case 'month': newMonth = numVal.slice(0, 2); setMonth(newMonth); break;
      case 'day': newDay = numVal.slice(0, 2); setDay(newDay); break;
      case 'hour': newHour = numVal.slice(0, 2); setHour(newHour); break;
      case 'minute': newMinute = numVal.slice(0, 2); setMinute(newMinute); break;
      default: break;
    }

    if (numVal.length >= maxLen && nextRef?.current) {
      nextRef.current.focus();
      nextRef.current.select();
    }

    const newValue = buildValue(newYear, newMonth, newDay, newHour, newMinute);
    if (newValue) {
      onChange(newValue);
    }
  };

  return (
    <div className="segmented-time-input">
      <span className="time-label">{label}</span>
      <div className="time-segments">
        <div className="seg-group date-group">
          <input
            ref={yearRef}
            type="text"
            value={year}
            onChange={(e) => handleChange('year', e.target.value, 4, monthRef)}
            placeholder="2026"
            maxLength={4}
            className="seg-input seg-year"
          />
          <span className="seg-dot">/</span>
          <input
            ref={monthRef}
            type="text"
            value={month}
            onChange={(e) => handleChange('month', e.target.value, 2, dayRef)}
            placeholder="01"
            maxLength={2}
            className="seg-input seg-num"
          />
          <span className="seg-dot">/</span>
          <input
            ref={dayRef}
            type="text"
            value={day}
            onChange={(e) => handleChange('day', e.target.value, 2, hourRef)}
            placeholder="01"
            maxLength={2}
            className="seg-input seg-num"
          />
        </div>
        <div className="seg-group time-group">
          <input
            ref={hourRef}
            type="text"
            value={hour}
            onChange={(e) => handleChange('hour', e.target.value, 2, minuteRef)}
            placeholder="00"
            maxLength={2}
            className="seg-input seg-num"
          />
          <span className="seg-colon">:</span>
          <input
            ref={minuteRef}
            type="text"
            value={minute}
            onChange={(e) => handleChange('minute', e.target.value, 2, null)}
            placeholder="00"
            maxLength={2}
            className="seg-input seg-num"
          />
        </div>
        <label className="calendar-btn">
          <input
            type="datetime-local"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          📅
        </label>
      </div>
    </div>
  );
};

// 格式化价值显示
const formatValue = (value) => {
  if (!value) return '0';
  const yuan = value / 1000;
  if (yuan >= 10000) {
    return (yuan / 10000).toFixed(1) + 'w';
  }
  return yuan.toFixed(yuan >= 100 ? 0 : 1);
};

// 解析 datetime-local 为时间戳
const parseDateTimeLocal = (str) => {
  if (!str) return null;
  return new Date(str).getTime() / 1000;
};

// 格式化日期时间为 input datetime-local 格式
const formatDateTimeLocal = (ts) => {
  if (!ts) return '';
  const date = new Date(ts * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

function GiftOverlaySettings() {
  const navigate = useNavigate();
  
  // 绑定状态检查
  const [accessStatus, setAccessStatus] = useState({ loading: true, hasAccess: false, reason: '' });

  // 房间相关
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // access token
  const [accessToken, setAccessToken] = useState('');
  const [tokenCopied, setTokenCopied] = useState(false);

  // 场次相关
  const [broadcasts, setBroadcasts] = useState([]);

  // 配置选项
  const [timeRangeType, setTimeRangeType] = useState('current');
  const [customStartTime, setCustomStartTime] = useState('');
  const [customEndTime, setCustomEndTime] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  // 可选礼物和用户
  const [availableGifts, setAvailableGifts] = useState({ guard: [], blindbox: {}, other: [] });
  const [activeUsers, setActiveUsers] = useState([]);
  const [giftsLoading, setGiftsLoading] = useState(false);

  // 已选择的礼物和用户
  const [selectedGiftIds, setSelectedGiftIds] = useState(new Set());
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());

  // 价值范围
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');

  // 显示选项
  const [sortBy, setSortBy] = useState('time');
  const [sortOrder, setSortOrder] = useState('desc');
  const [mergeGifts, setMergeGifts] = useState(true);
  const [showAvatar, setShowAvatar] = useState(true);
  const [useNickname, setUseNickname] = useState(false);

  // 滚动设置
  const [scrollMode, setScrollMode] = useState('none');
  const [scrollSpeed, setScrollSpeed] = useState(50);

  // 尺寸设置
  const [displaySize, setDisplaySize] = useState('medium');

  // 预览相关
  const [previewGifts, setPreviewGifts] = useState([]);
  const [userFaces, setUserFaces] = useState({});
  const [userGuardLevels, setUserGuardLevels] = useState({});
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSummary, setPreviewSummary] = useState({ total_records: 0, total_value: 0, unique_users: 0 });

  // 折叠状态
  const [expandedGroups, setExpandedGroups] = useState({
    guard: true,
    blindbox: true,
    other: true
  });

  // 舰长图标映射
  const guardIconMap = {
    '舰长': 'https://s1.hdslb.com/bfs/static/blive/live-pay-mono/relation/relation/assets/captain-Bjw5Byb5.png',
    '提督': 'https://s1.hdslb.com/bfs/static/blive/live-pay-mono/relation/relation/assets/supervisor-u43ElIjU.png',
    '总督': 'https://s1.hdslb.com/bfs/static/blive/live-pay-mono/relation/relation/assets/governor-DpDXKEdA.png'
  };

  // 获取礼物等级 class
  const getGiftLevelClass = (totalCoin) => {
    const value = totalCoin / 1000;
    if (value >= 100) return 'level-gold';
    if (value >= 15) return 'level-red';
    if (value >= 5) return 'level-purple';
    return '';
  };

  // 检查绑定状态
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const result = await bilibiliBindingService.checkFeatureAccess();
        setAccessStatus({
          loading: false,
          hasAccess: result.hasAccess,
          reason: result.reason || '',
          code: result.code || ''
        });
      } catch (err) {
        console.error('检查访问权限失败:', err);
        if (err.response?.status === 403) {
          setAccessStatus({
            loading: false,
            hasAccess: false,
            reason: err.response.data?.message || '您没有权限使用此功能',
            code: err.response.data?.code || ''
          });
        } else {
          setAccessStatus({
            loading: false,
            hasAccess: false,
            reason: '检查权限失败，请稍后重试'
          });
        }
      }
    };
    checkAccess();
  }, []);

  // 加载房间列表
  useEffect(() => {
    const loadRooms = async () => {
      try {
        setLoading(true);
        const config = await botService.getBotConfig();
        if (config && config.rooms) {
          const enabledRooms = config.rooms.filter(r => r.enabled);
          setRooms(enabledRooms);
          if (enabledRooms.length > 0) {
            // 使用 uid（主播UID）作为选中值，与 user_rooms 表存储保持一致
            const firstRoom = enabledRooms[0];
            setSelectedRoom(firstRoom.uid);
          }
        }
      } catch (err) {
        console.error('加载房间列表失败:', err);
        setError('加载房间列表失败');
      } finally {
        setLoading(false);
      }
    };

    loadRooms();
  }, []);

  // 加载配置和 token
  useEffect(() => {
    if (!selectedRoom) return;

    const loadConfig = async () => {
      try {
        // 获取 access token
        const tokenRes = await fetch(`${API_BASE}/gift-overlay/token/${selectedRoom}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (tokenRes.ok) {
          const tokenData = await tokenRes.json();
          setAccessToken(tokenData.access_token || '');
        }

        // 获取配置
        const configRes = await fetch(`${API_BASE}/gift-overlay/config/${selectedRoom}`);
        if (configRes.ok) {
          const config = await configRes.json();
          setTimeRangeType(config.time_mode || 'current');
          if (config.custom_start) {
            setCustomStartTime(formatDateTimeLocal(config.custom_start));
          }
          if (config.custom_end) {
            setCustomEndTime(formatDateTimeLocal(config.custom_end));
          }
          setSelectedGiftIds(new Set(config.gift_ids || []));
          setSelectedUserIds(new Set(config.user_ids || []));
          setMinValue(config.min_value ? (config.min_value / 1000).toString() : '');
          setMaxValue(config.max_value ? (config.max_value / 1000).toString() : '');
          setShowAvatar(config.show_avatar !== false);
          setMergeGifts(config.merge_gifts !== false);
          setUseNickname(config.use_nickname === true);
          setSortBy(config.sort_by || 'time');
          setSortOrder(config.sort_order || 'desc');
          setScrollMode(config.scroll_mode || 'none');
          setScrollSpeed(config.scroll_speed || 50);
          setDisplaySize(config.display_size || 'medium');
        }
      } catch (err) {
        console.error('加载配置失败:', err);
      }
    };

    loadConfig();
  }, [selectedRoom]);

  // 加载场次列表
  useEffect(() => {
    if (!selectedRoom) return;

    const loadBroadcasts = async () => {
      try {
        const data = await giftScreenshotService.getBroadcasts(selectedRoom, 10);
        setBroadcasts(data || []);
      } catch (err) {
        console.error('加载场次失败:', err);
      }
    };

    loadBroadcasts();
  }, [selectedRoom]);

  // 计算实际时间范围
  useEffect(() => {
    const now = Date.now() / 1000;
    let start = null;
    let end = now;

    switch (timeRangeType) {
      case 'current':
        if (broadcasts.length > 0) {
          start = broadcasts[0].start_time;
          end = broadcasts[0].end_time || now;
        }
        break;
      case 'previous':
        if (broadcasts.length > 1) {
          start = broadcasts[1].start_time;
          end = broadcasts[1].end_time || now;
        } else if (broadcasts.length > 0) {
          start = broadcasts[0].start_time;
          end = broadcasts[0].end_time || now;
        }
        break;
      case 'today':
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        start = today.getTime() / 1000;
        break;
      case 'week':
        start = now - 7 * 86400;
        break;
      case 'month':
        start = now - 30 * 86400;
        break;
      case 'custom':
        start = parseDateTimeLocal(customStartTime);
        end = parseDateTimeLocal(customEndTime) || now;
        break;
      default:
        break;
    }

    setStartTime(start);
    setEndTime(end);
  }, [timeRangeType, broadcasts, customStartTime, customEndTime]);

  // 加载可选礼物和用户
  useEffect(() => {
    if (!selectedRoom || !startTime || !endTime) return;

    const loadAvailable = async () => {
      try {
        setGiftsLoading(true);
        
        const [giftsData, usersData] = await Promise.all([
          giftScreenshotService.getAvailableGifts(selectedRoom, startTime, endTime),
          giftScreenshotService.getActiveUsers(selectedRoom, startTime, endTime)
        ]);

        setAvailableGifts(giftsData || { guard: [], blindbox: {}, other: [] });
        setActiveUsers(usersData || []);
      } catch (err) {
        console.error('加载可选项失败:', err);
      } finally {
        setGiftsLoading(false);
      }
    };

    loadAvailable();
  }, [selectedRoom, startTime, endTime]);

  // 加载预览数据
  useEffect(() => {
    if (!selectedRoom || !startTime || !endTime) return;

    const loadPreview = async () => {
      try {
        setPreviewLoading(true);
        
        // 使用 giftScreenshotService 获取礼物数据
        const data = await giftScreenshotService.getGifts(selectedRoom, {
          startTime,
          endTime,
          mergeGifts,
          useNickname,
          sortBy,
          sortOrder,
          giftIds: selectedGiftIds.size > 0 ? Array.from(selectedGiftIds) : null,
          userIds: selectedUserIds.size > 0 ? Array.from(selectedUserIds) : null,
          minValue: minValue ? parseFloat(minValue) * 1000 : 0,
          maxValue: maxValue ? parseFloat(maxValue) * 1000 : null
        });

        setPreviewGifts(data.gifts || []);
        setPreviewSummary({
          total_records: data.summary?.total_records || data.gifts?.length || 0,
          total_value: data.summary?.total_value || 0,
          unique_users: data.summary?.unique_users || 0
        });

        // 获取用户头像和大航海等级
        if (showAvatar && data.gifts?.length > 0) {
          const uids = [...new Set(data.gifts.map(g => g.uid))];
          try {
            const [faces, guardLevels] = await Promise.all([
              giftScreenshotService.getUserFaces(uids),
              giftScreenshotService.getUserGuardLevels(selectedRoom, uids)
            ]);
            setUserFaces(faces || {});
            setUserGuardLevels(guardLevels || {});
          } catch (err) {
            console.error('获取头像/大航海等级失败:', err);
          }
        }
      } catch (err) {
        console.error('加载预览数据失败:', err);
      } finally {
        setPreviewLoading(false);
      }
    };

    // 使用 debounce 避免频繁请求
    const timer = setTimeout(loadPreview, 300);
    return () => clearTimeout(timer);
  }, [selectedRoom, startTime, endTime, mergeGifts, useNickname, sortBy, sortOrder, selectedGiftIds, selectedUserIds, minValue, maxValue, showAvatar]);

  // 保存配置
  const saveConfig = async () => {
    if (!selectedRoom) return;

    try {
      setSaving(true);
      setSaveMessage(null);

      const config = {
        time_mode: timeRangeType,
        custom_start: timeRangeType === 'custom' ? parseDateTimeLocal(customStartTime) : null,
        custom_end: timeRangeType === 'custom' ? parseDateTimeLocal(customEndTime) : null,
        gift_ids: selectedGiftIds.size > 0 ? Array.from(selectedGiftIds) : null,
        user_ids: selectedUserIds.size > 0 ? Array.from(selectedUserIds) : null,
        min_value: minValue ? parseFloat(minValue) * 1000 : 0,
        max_value: maxValue ? parseFloat(maxValue) * 1000 : null,
        show_avatar: showAvatar,
        merge_gifts: mergeGifts,
        use_nickname: useNickname,
        sort_by: sortBy,
        sort_order: sortOrder,
        scroll_mode: scrollMode,
        scroll_speed: scrollSpeed,
        display_size: displaySize
      };

      const response = await fetch(`${API_BASE}/gift-overlay/config/${selectedRoom}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        setSaveMessage({ type: 'success', text: '配置已保存' });
      } else {
        const data = await response.json();
        setSaveMessage({ type: 'error', text: data.message || '保存失败' });
      }
    } catch (err) {
      console.error('保存配置失败:', err);
      setSaveMessage({ type: 'error', text: '保存失败' });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  // 重新生成 token
  const regenerateToken = async () => {
    if (!selectedRoom) return;

    try {
      const response = await fetch(`${API_BASE}/gift-overlay/token/${selectedRoom}/regenerate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAccessToken(data.access_token);
        setSaveMessage({ type: 'success', text: '已重新生成链接' });
        setTimeout(() => setSaveMessage(null), 3000);
      }
    } catch (err) {
      console.error('重新生成 token 失败:', err);
    }
  };

  // 复制链接
  const copyLink = () => {
    if (!accessToken) return;
    const url = `${window.location.origin}/live/gifts/${accessToken}`;
    navigator.clipboard.writeText(url);
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2000);
  };

  // 全选/取消全选礼物
  const toggleSelectAllGifts = () => {
    const allGiftIds = new Set();
    availableGifts.guard?.forEach(g => allGiftIds.add(g.gift_id));
    Object.values(availableGifts.blindbox || {}).forEach(gifts => {
      gifts.forEach(g => allGiftIds.add(g.gift_id));
    });
    availableGifts.other?.forEach(g => allGiftIds.add(g.gift_id));

    if (selectedGiftIds.size === allGiftIds.size) {
      setSelectedGiftIds(new Set());
    } else {
      setSelectedGiftIds(allGiftIds);
    }
  };

  // 切换礼物选择
  const toggleGiftSelection = (giftId) => {
    setSelectedGiftIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(giftId)) {
        newSet.delete(giftId);
      } else {
        newSet.add(giftId);
      }
      return newSet;
    });
  };

  // 切换用户选择
  const toggleUserSelection = (uid) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(uid)) {
        newSet.delete(uid);
      } else {
        newSet.add(uid);
      }
      return newSet;
    });
  };

  // 切换分组展开状态
  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  // 获取 OBS 链接
  const obsUrl = accessToken ? `${window.location.origin}/live/gifts/${accessToken}` : '';

  // 检查绑定状态 loading
  if (accessStatus.loading) {
    return <div className="gift-screenshot-page"><p>检查权限...</p></div>;
  }

  // 没有访问权限，重定向到首页
  if (!accessStatus.hasAccess) {
    navigate('/');
    return null;
  }

  if (loading) {
    return <div className="gift-screenshot-page"><p>加载中...</p></div>;
  }

  if (error) {
    return <div className="gift-screenshot-page"><p className="error">{error}</p></div>;
  }

  return (
    <div className="gift-screenshot-page">
      {/* 页头 */}
      <div className="page-header">
        <h1>
          <BackButton />
          OBS 礼物展示设置
        </h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Link to="/admin/gift-screenshot" className="link-btn" style={{ background: '#6366f1' }}>
            礼物截图
          </Link>
          <Link to="/admin/blindbox" className="link-btn">
            盲盒统计
          </Link>
        </div>
      </div>

      {/* OBS 链接区域 */}
      <div className="section" style={{ marginBottom: '1.5rem' }}>
        <div className="section-title">📺 OBS 浏览器源链接</div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={obsUrl}
            readOnly
            style={{
              flex: 1,
              minWidth: '300px',
              padding: '0.6rem',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '0.9rem',
              background: '#f5f5f5'
            }}
          />
          <button
            onClick={copyLink}
            className="export-btn"
            style={{ width: 'auto', marginTop: 0, padding: '0.6rem 1rem' }}
          >
            {tokenCopied ? '✓ 已复制' : '复制链接'}
          </button>
          <button
            onClick={regenerateToken}
            className="export-btn"
            style={{ width: 'auto', marginTop: 0, padding: '0.6rem 1rem', background: '#6366f1' }}
          >
            重新生成
          </button>
          {accessToken && (
            <a
              href={`${obsUrl}?preview=1`}
              target="_blank"
              rel="noopener noreferrer"
              className="export-btn"
              style={{ 
                width: 'auto', 
                marginTop: 0, 
                padding: '0.6rem 1rem', 
                background: '#22c55e',
                textDecoration: 'none'
              }}
            >
              预览
            </a>
          )}
        </div>
        <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
          将此链接添加为 OBS 浏览器源，即可实时展示礼物。建议宽度 400px。
        </p>
      </div>

      {/* 顶部设置区 */}
      <div className="top-settings">
        {/* 房间选择 + 时间范围 */}
        <div className="settings-row">
          <div className="setting-item">
            <label>房间：</label>
            <select
              value={selectedRoom || ''}
              onChange={(e) => setSelectedRoom(e.target.value)}
            >
              {rooms.map(room => {
                const displayRoomId = room.anchor_info?.room_id || room.uid;
                const roomName = room.anchor_info?.uname || room.uid;
                return (
                  <option key={room.uid} value={room.uid}>
                    {roomName} ({displayRoomId})
                  </option>
                );
              })}
            </select>
          </div>

          <div className="setting-item time-range-setting">
            <label>时间范围：</label>
            <div className="time-range-buttons compact">
              {[
                { value: 'current', label: '本场' },
                { value: 'previous', label: '上场' },
                { value: 'today', label: '今日' },
                { value: 'week', label: '本周' },
                { value: 'month', label: '本月' },
                { value: 'custom', label: '自定义' }
              ].map(option => (
                <button
                  key={option.value}
                  className={timeRangeType === option.value ? 'active' : ''}
                  onClick={() => setTimeRangeType(option.value)}
                  disabled={option.value === 'previous' && broadcasts.length < 2}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 自定义时间 */}
        {timeRangeType === 'custom' && (
          <div className="custom-time-row">
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              <SegmentedTimeInput
                label="开始"
                value={customStartTime}
                onChange={setCustomStartTime}
              />
              <SegmentedTimeInput
                label="结束"
                value={customEndTime}
                onChange={setCustomEndTime}
              />
            </div>
          </div>
        )}

        {/* 显示选项行 */}
        <div className="settings-row display-options-row">
          <div className="setting-item checkbox-item">
            <input
              type="checkbox"
              id="mergeGifts"
              checked={mergeGifts}
              onChange={(e) => setMergeGifts(e.target.checked)}
            />
            <label htmlFor="mergeGifts">合并礼物</label>
          </div>

          <div className="setting-item checkbox-item">
            <input
              type="checkbox"
              id="showAvatar"
              checked={showAvatar}
              onChange={(e) => setShowAvatar(e.target.checked)}
            />
            <label htmlFor="showAvatar">显示头像</label>
          </div>

          <div className="setting-item checkbox-item">
            <input
              type="checkbox"
              id="useNickname"
              checked={useNickname}
              onChange={(e) => setUseNickname(e.target.checked)}
            />
            <label htmlFor="useNickname">用昵称</label>
          </div>

          <div className="setting-item">
            <label>排序：</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="time">按时间</option>
              <option value="value">按价值</option>
              <option value="count">按数量</option>
            </select>
          </div>

          <div className="setting-item">
            <label>顺序：</label>
            <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
              <option value="desc">降序</option>
              <option value="asc">升序</option>
            </select>
          </div>

          <div className="setting-item value-filter">
            <label>价值：</label>
            <input
              type="number"
              placeholder="最小"
              value={minValue}
              onChange={(e) => setMinValue(e.target.value)}
            />
            <span>~</span>
            <input
              type="number"
              placeholder="最大"
              value={maxValue}
              onChange={(e) => setMaxValue(e.target.value)}
            />
            <span>元</span>
          </div>
        </div>

        {/* 滚动和尺寸设置 */}
        <div className="settings-row display-options-row" style={{ marginTop: '0.5rem' }}>
          <div className="setting-item">
            <label>滚动模式：</label>
            <select value={scrollMode} onChange={(e) => setScrollMode(e.target.value)}>
              <option value="none">不滚动</option>
              <option value="loop">循环滚动</option>
              <option value="bounce">往复滚动</option>
            </select>
          </div>

          <div className="setting-item">
            <label>滚动速度：</label>
            <input
              type="range"
              min="10"
              max="200"
              value={scrollSpeed}
              onChange={(e) => setScrollSpeed(parseInt(e.target.value))}
              style={{ width: '100px' }}
            />
            <span style={{ minWidth: '50px' }}>{scrollSpeed} px/s</span>
          </div>

          <div className="setting-item">
            <label>尺寸：</label>
            <select value={displaySize} onChange={(e) => setDisplaySize(e.target.value)}>
              <option value="small">小</option>
              <option value="medium">中</option>
              <option value="large">大</option>
            </select>
          </div>
        </div>
      </div>

      {/* 主布局 */}
      <div className="main-layout">
        {/* 左侧 - 预览区 */}
        <div className="left-panel">
          <div className="section preview-section">
            <h2 className="section-title">预览</h2>
            <div className="preview-container transparent-bg">
              <div className="gift-preview" style={{ '--theme-color': '#ff6b9d' }}>
                {previewLoading ? (
                  <div className="preview-loading">加载中...</div>
                ) : previewGifts.length === 0 ? (
                  <div className="preview-empty">暂无礼物数据</div>
                ) : (
                  <div className="gift-list">
                    {previewGifts.slice(0, 20).map((gift, index) => {
                      const giftIconUrl = gift.img_basic || guardIconMap[gift.gift_name];
                      const guardFrameUrl = guardFrameMap[userGuardLevels[gift.uid]];

                      return (
                        <div
                          key={gift.id || `${gift.gift_id}-${gift.uid}-${index}`}
                          className={`bubble-wrapper ${getGiftLevelClass(gift.num * gift.price)}`}
                        >
                          <div className="bubble-bg"></div>
                          <div className="bubble-content">
                            {showAvatar && (
                              <div className="avatar-wrapper">
                                {userFaces[gift.uid] ? (
                                  <img
                                    className="user-avatar"
                                    src={userFaces[gift.uid]}
                                    alt=""
                                    referrerPolicy="no-referrer"
                                    crossOrigin="anonymous"
                                  />
                                ) : (
                                  <div className="avatar-placeholder" />
                                )}
                                {guardFrameUrl && (
                                  <img
                                    className="guard-frame"
                                    src={guardFrameUrl}
                                    alt=""
                                    referrerPolicy="no-referrer"
                                  />
                                )}
                              </div>
                            )}
                            <div className="gift-info">
                              <div className="info-row">
                                <span className="user-name">{gift.username}</span>
                                <span className="action-text">投喂</span>
                              </div>
                              <span className="gift-name">{gift.gift_name}</span>
                            </div>
                            {giftIconUrl && (
                              <img
                                src={giftIconUrl}
                                alt={gift.gift_name}
                                className="gift-icon"
                                referrerPolicy="no-referrer"
                              />
                            )}
                            <div className="combo-count">
                              <div className="count-part part-x" data-text="x">
                                <span className="gradient-text">x</span>
                              </div>
                              <div className="count-part part-num" data-text={String(gift.num || 1)}>
                                <span className="gradient-text">{gift.num || 1}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* 统计信息 */}
            <div className="summary-info">
              <span>共 {previewSummary.total_records || 0} 条记录</span>
              <span>总价值 ￥{formatValue(previewSummary.total_value)}</span>
              <span>{previewSummary.unique_users || 0} 位用户</span>
            </div>

            {/* 保存按钮 */}
            <button
              onClick={saveConfig}
              disabled={saving}
              className="export-btn"
            >
              {saving ? '保存中...' : '💾 保存配置'}
            </button>
            {saveMessage && (
              <p style={{ 
                marginTop: '0.5rem', 
                textAlign: 'center',
                color: saveMessage.type === 'success' ? '#22c55e' : '#ef4444',
                fontSize: '0.9rem'
              }}>
                {saveMessage.text}
              </p>
            )}
          </div>
        </div>

        {/* 右侧 - 筛选配置 */}
        <div className="right-panel">
          {/* 礼物筛选 */}
          <div className="section">
            <div className="section-title">
              🎁 礼物筛选
              {giftsLoading && <span className="loading-hint">加载中...</span>}
              <button
                onClick={toggleSelectAllGifts}
                style={{
                  marginLeft: 'auto',
                  padding: '0.3rem 0.8rem',
                  fontSize: '0.85rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                {selectedGiftIds.size === 0 ? '全选' : '取消全选'}
              </button>
            </div>
            <div className="section-hint">
              不选择任何礼物表示显示全部
            </div>

            {/* 大航海 */}
            {availableGifts.guard?.length > 0 && (
              <div className="gift-group">
                <div 
                  className="group-header"
                  onClick={() => toggleGroup('guard')}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}
                >
                  <span>{expandedGroups.guard ? '▼' : '▶'}</span>
                  <span style={{ fontWeight: 600 }}>🚢 大航海</span>
                  <span style={{ color: '#888', fontSize: '0.85rem' }}>
                    ({availableGifts.guard.length})
                  </span>
                </div>
                {expandedGroups.guard && (
                  <div className="gift-items" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {availableGifts.guard.map(gift => {
                      const iconUrl = gift.img_basic || guardIconMap[gift.gift_name];
                      return (
                        <label
                          key={gift.gift_id}
                          className="checkbox-item"
                          style={{ 
                            padding: '0.4rem 0.8rem',
                            background: selectedGiftIds.has(gift.gift_id) ? 'linear-gradient(135deg, #ff6b9d 0%, #c44569 100%)' : 'white',
                            color: selectedGiftIds.has(gift.gift_id) ? 'white' : 'inherit',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedGiftIds.has(gift.gift_id)}
                            onChange={() => toggleGiftSelection(gift.gift_id)}
                            style={{ display: 'none' }}
                          />
                          {iconUrl && (
                            <img 
                              src={iconUrl} 
                              alt=""
                              style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                              referrerPolicy="no-referrer"
                            />
                          )}
                          {gift.gift_name} ({formatValue(gift.total_value)})
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* 盲盒 */}
            {Object.keys(availableGifts.blindbox || {}).length > 0 && (
              <div className="gift-group" style={{ marginTop: '1rem' }}>
                <div 
                  className="group-header"
                  onClick={() => toggleGroup('blindbox')}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}
                >
                  <span>{expandedGroups.blindbox ? '▼' : '▶'}</span>
                  <span style={{ fontWeight: 600 }}>📦 盲盒礼物</span>
                </div>
                {expandedGroups.blindbox && Object.entries(availableGifts.blindbox).map(([boxName, gifts]) => (
                  <div key={boxName} style={{ marginBottom: '0.75rem' }}>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.3rem' }}>
                      {boxName}
                    </div>
                    <div className="gift-items" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {gifts.map(gift => (
                        <label
                          key={gift.gift_id}
                          className="checkbox-item"
                          style={{ 
                            padding: '0.4rem 0.8rem',
                            background: selectedGiftIds.has(gift.gift_id) ? 'linear-gradient(135deg, #ff6b9d 0%, #c44569 100%)' : 'white',
                            color: selectedGiftIds.has(gift.gift_id) ? 'white' : 'inherit',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedGiftIds.has(gift.gift_id)}
                            onChange={() => toggleGiftSelection(gift.gift_id)}
                            style={{ display: 'none' }}
                          />
                          {gift.img_basic && (
                            <img 
                              src={gift.img_basic} 
                              alt=""
                              style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                              referrerPolicy="no-referrer"
                            />
                          )}
                          {gift.gift_name} ({formatValue(gift.total_value)})
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 其他礼物 */}
            {availableGifts.other?.length > 0 && (
              <div className="gift-group" style={{ marginTop: '1rem' }}>
                <div 
                  className="group-header"
                  onClick={() => toggleGroup('other')}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}
                >
                  <span>{expandedGroups.other ? '▼' : '▶'}</span>
                  <span style={{ fontWeight: 600 }}>🎁 其他礼物</span>
                  <span style={{ color: '#888', fontSize: '0.85rem' }}>
                    ({availableGifts.other.length})
                  </span>
                </div>
                {expandedGroups.other && (
                  <div className="gift-items" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {availableGifts.other.slice(0, 50).map(gift => (
                      <label
                        key={gift.gift_id}
                        className="checkbox-item"
                        style={{ 
                          padding: '0.4rem 0.8rem',
                          background: selectedGiftIds.has(gift.gift_id) ? 'linear-gradient(135deg, #ff6b9d 0%, #c44569 100%)' : 'white',
                          color: selectedGiftIds.has(gift.gift_id) ? 'white' : 'inherit',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.4rem'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedGiftIds.has(gift.gift_id)}
                          onChange={() => toggleGiftSelection(gift.gift_id)}
                          style={{ display: 'none' }}
                        />
                        {gift.img_basic && (
                          <img 
                            src={gift.img_basic} 
                            alt=""
                            style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                            referrerPolicy="no-referrer"
                          />
                        )}
                        {gift.gift_name} ({formatValue(gift.total_value)})
                      </label>
                    ))}
                    {availableGifts.other.length > 50 && (
                      <span style={{ color: '#888', fontSize: '0.85rem', padding: '0.4rem' }}>
                        ...还有 {availableGifts.other.length - 50} 个
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 用户筛选 */}
          <div className="section">
            <div className="section-title">
              👥 用户筛选
              <button
                onClick={() => setSelectedUserIds(new Set())}
                style={{
                  marginLeft: 'auto',
                  padding: '0.3rem 0.8rem',
                  fontSize: '0.85rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                清除选择
              </button>
            </div>
            <div className="section-hint">
              不选择任何用户表示显示全部用户的礼物
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', maxHeight: '200px', overflowY: 'auto' }}>
              {activeUsers.slice(0, 100).map(user => (
                <label
                  key={user.uid}
                  className="checkbox-item"
                  style={{ 
                    padding: '0.4rem 0.8rem',
                    background: selectedUserIds.has(user.uid) ? 'linear-gradient(135deg, #ff6b9d 0%, #c44569 100%)' : 'white',
                    color: selectedUserIds.has(user.uid) ? 'white' : 'inherit'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedUserIds.has(user.uid)}
                    onChange={() => toggleUserSelection(user.uid)}
                    style={{ display: 'none' }}
                  />
                  {user.username} ({formatValue(user.total_value)})
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GiftOverlaySettings;
